import { ComponentType, FC, useMemo } from 'react';

import {
    ChartMetricOptions,
    ChartPrecisionOptions,
    ChartViewOptions,
} from '@constants/ChartConstants';
import moment, { Moment } from 'moment';

import { DeprecatedAdvancedChart } from '@core/charts/components/AdvancedChart';
import { useChartContext } from '@core/charts/components/AdvancedChart/ChartContext';
import { ComparisonChartProvider } from '@core/charts/components/AdvancedComparisonChart/ComparisonChartContext';

import PageHeader from '@components/layout/PageHeader';
import Paper from '@components/layout/Paper';
import Toolbar from '@components/toolbar/Toolbar';

import { IAccount } from '@app/types/AccountsTypes';
import { IChartDataSettings, TChartDataPrecision } from '@app/types/ChartDataTypes';
import { IPosLocation } from '@app/types/PosTypes';

import DashboardUserCharts from '../components/DashboardUserCharts';
import { useDashboardContext } from '../contexts/DashboardContext';

import './RetailerDashboardView.scss';

export interface RetailerDashboardViewProps {
    account: IAccount;
}
export const RetailerDashboardView: FC<RetailerDashboardViewProps> = ({ account }) => {
    // This should be replaced with a `useRetailerChartContext` sort of hook
    const { chartSettings, updateChartSettings, onUpdateChartSetting } = useChartContext();

    const locationOptions = useMemo(() => account?.locations ?? [], [account]);

    const selectedLocations = useMemo(() => {
        return locationOptions.filter(({ value }) => chartSettings.locationIds.includes(value));
    }, [chartSettings.locationIds, locationOptions]);

    const { setDateRange: setCustomContextDateRange } = useDashboardContext();

    const updateDateRange = (dateStart: Moment | null, dateEnd: Moment | null) => {
        setCustomContextDateRange({
            dateStart,
            dateEnd,
        });
        updateChartSettings({ dateStart, dateEnd });
    };

    const updateFrequency = (value: TChartDataPrecision) => {
        updateChartSettings({
            precision: value,
        });
    };

    const updateSelectedLocations = (selected: IPosLocation[]) => {
        if (selected.length > 0) {
            updateChartSettings({
                locationIds: selected.map((obj) => obj.value),
            });
        }
    };

    const pageHeaderTitle = `${account?.name} Dashboard`;
    const pageMetaTitle = `${account?.name} Dashboard`;

    return (
        <div className={`dashboard-view dashboard-${account?.type}-view`}>
            <PageHeader title={pageHeaderTitle} metaTitle={pageMetaTitle} />

            <Toolbar scrollOnMobile>
                <Toolbar.DateRangeSelector
                    id="dashboard-date-range"
                    dateStart={chartSettings.dateStart}
                    dateEnd={chartSettings.dateEnd}
                    onApply={updateDateRange}
                    alwaysShowDates
                />
                <Toolbar.Dropdown
                    label="Frequency"
                    value={chartSettings.precision}
                    onSelect={updateFrequency}
                    options={ChartPrecisionOptions}
                />

                <Toolbar.DropdownListSelector
                    label="Locations"
                    selected={selectedLocations || []}
                    onApply={updateSelectedLocations}
                    options={locationOptions || []}
                    clear={{
                        active: (selectedLocations || []).length !== (locationOptions || []).length,
                        onClear: () => {
                            updateSelectedLocations(locationOptions);
                        },
                    }}
                />

                <Toolbar.Dropdown
                    label={null}
                    className="toolbar-group-end"
                    value={chartSettings.type}
                    onSelect={onUpdateChartSetting('type')}
                    options={ChartViewOptions}
                />
            </Toolbar>

            <Paper className="section">
                <Toolbar>
                    <Toolbar.Dropdown
                        className="toolbar-group-start section-title"
                        label={null}
                        value={chartSettings.metric}
                        onSelect={onUpdateChartSetting('metric')}
                        options={ChartMetricOptions.filter(
                            ({ value }) =>
                                !['units_per_transaction', 'percent_of_total_sales'].includes(
                                    value,
                                ),
                        )}
                    />
                </Toolbar>

                {/**
                 * This should be replaced with some sort of `RetailerChart` component that uses
                 * a new `RetailerChartContext` and the `AdvancedChart` component
                 */}
                <DeprecatedAdvancedChart />
            </Paper>

            <Paper className="section">
                <DashboardUserCharts
                    retailerAccountId={account._id}
                    chartSettings={chartSettings}
                />
            </Paper>
        </div>
    );
};

const withRetailerChartProvider =
    (DashboardComponent: ComponentType<RetailerDashboardViewProps>) =>
    ({ account }: RetailerDashboardViewProps) => {
        const dateStart = moment().subtract(30, 'days').toDate();
        const dateEnd = moment().toDate();
        const { dateStart: customDateStart, dateEnd: customDateEnd } = useDashboardContext();

        const initialChartSettings: IChartDataSettings = {
            locationIds: (account?.locations ?? []).map(({ value }) => value),
            metric: 'total_sales',
            precision: 'day',
            type: 'bar',
            dateStart: customDateStart || dateStart,
            dateEnd: customDateEnd || dateEnd,
            breakdown: 'none',
        };

        return (
            <ComparisonChartProvider
                retailerAccountId={account?._id ?? ''}
                initialSettings={initialChartSettings}
            >
                <DashboardComponent account={account} />
            </ComparisonChartProvider>
        );
    };

export default withRetailerChartProvider(RetailerDashboardView);
